import { collection, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

// تعريف نوع الإعدادات العامة
export interface SystemSettings {
  companyName: string;
  companyLogo?: string;
  companyFavicon?: string;
  companyAddress: string;
  companyPhone: string;
  vatNumber: string;
  currency: string;
  language: string;
  enableNotifications: boolean;
  enableDarkMode: boolean;
  showVat: boolean;
  vatPercentage: number;
  emailNotifications: boolean;
  backupEnabled: boolean;
  backupFrequency: 'daily' | 'weekly' | 'monthly';
  // إعدادات رقم الهاتف
  phoneValidationEnabled: boolean;
  phoneDigitsCount: number;
  // إعدادات رسائل الواتساب
  whatsappEnabled: boolean;
  whatsappCountryCode: string;
  whatsappCountryName: string;
  whatsappAutoSendOnCreate: boolean; // إرسال رسالة تلقائياً عند إنشاء طلب صيانة
  whatsappMessages: {
    deviceReceived: string;
    deviceInProgress: string;
    deviceCompleted: string;
    deviceUrgent: string;
  };
  // إعدادات الطباعة
  printSettings?: {
    defaultPrintType: 'a4' | 'thermal';
    enablePrintOptions: boolean;
    autoOpenPrintDialog: boolean;
    thermalPrinterWidth: number;
    a4PageSize: 'A4' | 'Letter';
    printQuality: 'draft' | 'normal' | 'high';
    enablePrintPreview: boolean;
    savePrintHistory: boolean;
    maxPrintHistory: number;
  };
}

// الإعدادات الافتراضية
export const DEFAULT_SETTINGS: SystemSettings = {
  companyName: 'اليكترونيات السواح',
  companyAddress: 'العنوان، المدينة، البلد',
  companyPhone: '0123456789',
  vatNumber: '123456789012345',
  currency: 'EGP',
  language: 'ar',
  enableNotifications: true,
  enableDarkMode: false,
  showVat: true,
  vatPercentage: 15,
  emailNotifications: true,
  backupEnabled: true,
  backupFrequency: 'daily',
  // إعدادات رقم الهاتف الافتراضية (مصر)
  phoneValidationEnabled: true,
  phoneDigitsCount: 10,
  // إعدادات رسائل الواتساب الافتراضية
  whatsappEnabled: true,
  whatsappCountryCode: '20',
  whatsappCountryName: 'مصر',
  whatsappAutoSendOnCreate: true, // إرسال رسالة تلقائياً عند إنشاء طلب صيانة
  whatsappMessages: {
    deviceReceived: 'مرحباً {customerName}، تم استلام جهازك {deviceBrand} وهو الآن قيد العمل. التكلفة المتوقعة: {cost}. سنتواصل معك عند اكتمال الإصلاح من فضلك وافق بنعم حتي تتلقي اشعارات بانتهاء الصيانه.',
    deviceInProgress: 'مرحباً {customerName}، بعد فحص جهازك {deviceBrand}، التكلفة النهائية للإصلاح هي: {cost}. يرجى الرد بالموافقة للمتابعة أو الرفض لإرجاع الجهاز كما هو.',
    deviceCompleted: 'مرحباً {customerName}، تم الانتهاء من إصلاح جهازك {deviceBrand}، يرجى المرور لاستلامه في أقرب وقت ممكن. شكراً لثقتكم بنا الكترونيات السواح باب اللوق 01025654218.',
    deviceUrgent: 'مرحباً {customerName}، نذكرك بأن جهازك {deviceBrand} {deviceSize} جاهز للاستلام منذ يومين. يرجى المرور لاستلامه في أقرب وقت ممكن لتجنب أي رسوم إضافية. شكراً لتفهمكم.'
  }
};

// الحصول على مرجع مجموعة الإعدادات
const settingsCollection = () => collection(db, 'settings');

/**
 * الحصول على إعدادات النظام
 * @returns إعدادات النظام
 */
export const getSystemSettings = async (): Promise<SystemSettings> => {
  try {
    let settings: SystemSettings = { ...DEFAULT_SETTINGS };
    let settingsFound = false;

    // محاولة الحصول على الإعدادات من localStorage أولاً
    const savedSettings = localStorage.getItem('systemSettings');
    if (savedSettings) {
      try {
        const parsedSettings = JSON.parse(savedSettings) as SystemSettings;
        // دمج الإعدادات المحفوظة مع الإعدادات الافتراضية لضمان وجود جميع الحقول
        settings = { ...DEFAULT_SETTINGS, ...parsedSettings };

        // التأكد من أن القيم الرقمية هي numbers وليس strings
        if (typeof settings.phoneDigitsCount === 'string') {
          settings.phoneDigitsCount = parseInt(settings.phoneDigitsCount, 10) || DEFAULT_SETTINGS.phoneDigitsCount;
        }
        if (typeof settings.vatPercentage === 'string') {
          settings.vatPercentage = parseInt(settings.vatPercentage, 10) || DEFAULT_SETTINGS.vatPercentage;
        }

        settingsFound = true;
        console.log('Settings loaded from localStorage:', settings);
        console.log('WhatsApp auto-send setting from localStorage:', settings.whatsappAutoSendOnCreate);
      } catch (error) {
        console.error('Error parsing saved settings from localStorage:', error);
      }
    }

    // إذا لم تكن الإعدادات موجودة في localStorage، حاول الحصول عليها من Firestore
    if (!settingsFound) {
      const settingsDoc = await getDoc(doc(settingsCollection(), 'general'));

      if (settingsDoc.exists()) {
        const firestoreSettings = settingsDoc.data() as SystemSettings;
        // دمج الإعدادات من Firestore مع الإعدادات الافتراضية لضمان وجود جميع الحقول
        settings = { ...DEFAULT_SETTINGS, ...firestoreSettings };

        // التأكد من أن القيم الرقمية هي numbers وليس strings
        if (typeof settings.phoneDigitsCount === 'string') {
          settings.phoneDigitsCount = parseInt(settings.phoneDigitsCount, 10) || DEFAULT_SETTINGS.phoneDigitsCount;
        }
        if (typeof settings.vatPercentage === 'string') {
          settings.vatPercentage = parseInt(settings.vatPercentage, 10) || DEFAULT_SETTINGS.vatPercentage;
        }

        settingsFound = true;

        // حفظ الإعدادات في localStorage للاستخدام المستقبلي
        localStorage.setItem('systemSettings', JSON.stringify(settings));
        console.log('Settings loaded from Firestore:', settings);
        console.log('WhatsApp auto-send setting from Firestore:', settings.whatsappAutoSendOnCreate);
      }
    }

    // إذا لم يتم العثور على الإعدادات، استخدم الإعدادات الافتراضية وقم بحفظها
    if (!settingsFound) {
      console.log('No settings found, using defaults:', DEFAULT_SETTINGS);
      // حفظ الإعدادات الافتراضية في localStorage
      localStorage.setItem('systemSettings', JSON.stringify(DEFAULT_SETTINGS));

      // حفظ الإعدادات الافتراضية في Firestore
      try {
        await setDoc(doc(settingsCollection(), 'general'), {
          ...DEFAULT_SETTINGS,
          updatedAt: new Date()
        });
      } catch (error) {
        console.error('Error saving default settings to Firestore:', error);
      }
    }

    console.log('📋 الإعدادات النهائية المُحملة:', settings);
    console.log('📱 إعداد الإرسال التلقائي النهائي:', settings.whatsappAutoSendOnCreate);

    return settings;
  } catch (error) {
    console.error('Error getting system settings:', error);
    return DEFAULT_SETTINGS;
  }
};

/**
 * حفظ إعدادات النظام
 * @param settings إعدادات النظام
 */
export const saveSystemSettings = async (settings: SystemSettings): Promise<void> => {
  try {
    console.log('💾 حفظ إعدادات النظام:', settings);
    console.log('📱 إعداد الإرسال التلقائي للواتساب:', settings.whatsappAutoSendOnCreate);

    // حفظ الإعدادات في localStorage
    localStorage.setItem('systemSettings', JSON.stringify(settings));

    // تحويل الإعدادات إلى كائن عادي ليتوافق مع Firestore
    const settingsData = {
      companyName: settings.companyName,
      companyLogo: settings.companyLogo || null,
      companyFavicon: settings.companyFavicon || null,
      companyAddress: settings.companyAddress,
      companyPhone: settings.companyPhone,
      vatNumber: settings.vatNumber,
      currency: settings.currency,
      language: settings.language,
      enableNotifications: settings.enableNotifications,
      enableDarkMode: settings.enableDarkMode,
      showVat: settings.showVat,
      vatPercentage: settings.vatPercentage,
      emailNotifications: settings.emailNotifications,
      backupEnabled: settings.backupEnabled,
      backupFrequency: settings.backupFrequency,
      // إعدادات رقم الهاتف
      phoneValidationEnabled: settings.phoneValidationEnabled,
      phoneDigitsCount: settings.phoneDigitsCount,
      // إعدادات رسائل الواتساب
      whatsappEnabled: settings.whatsappEnabled,
      whatsappCountryCode: settings.whatsappCountryCode,
      whatsappCountryName: settings.whatsappCountryName,
      whatsappAutoSendOnCreate: settings.whatsappAutoSendOnCreate,
      whatsappMessages: {
        deviceReceived: settings.whatsappMessages.deviceReceived,
        deviceInProgress: settings.whatsappMessages.deviceInProgress,
        deviceCompleted: settings.whatsappMessages.deviceCompleted,
        deviceUrgent: settings.whatsappMessages.deviceUrgent
      },
      updatedAt: new Date()
    };

    // حفظ الإعدادات في Firestore
    const settingsRef = doc(settingsCollection(), 'general');
    const settingsDoc = await getDoc(settingsRef);

    if (settingsDoc.exists()) {
      await updateDoc(settingsRef, settingsData);
      console.log('✅ تم تحديث الإعدادات في Firestore');
    } else {
      await setDoc(settingsRef, settingsData);
      console.log('✅ تم إنشاء الإعدادات في Firestore');
    }

    console.log('💾 الإعدادات المحفوظة في Firestore:', settingsData);
  } catch (error) {
    console.error('Error saving system settings:', error);
    throw error;
  }
};

// دالة تحديث إعدادات النظام
export const updateSystemSettings = async (settings: Partial<SystemSettings>): Promise<void> => {
  try {
    const settingsRef = doc(settingsCollection(), 'general');
    await updateDoc(settingsRef, {
      ...settings,
      lastUpdated: new Date()
    });
  } catch (error) {
    console.error('Error updating system settings:', error);
    throw error;
  }
};

// Crear objeto para exportar por defecto
const systemSettingsService = {
  getSystemSettings,
  saveSystemSettings,
  updateSystemSettings,
  DEFAULT_SETTINGS
};

export default systemSettingsService;
